import {computed, inject, Injectable, signal} from '@angular/core';
import {StoreAssignments} from "../_models/store-assignments";
import {LoggerService} from "./logger.service";
import {environment} from '@envs/environment';
import {HttpClient} from "@angular/common/http";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {RequestHandlerService} from "@services/request-handler.service";
import {Coordinate} from "@app/_models/coordinate";
import {AddressService} from "@services/address.service";

@Injectable({
    providedIn: 'root'
})
export class StoreAssignmentService {
    private _http = inject(HttpClient);
    private _logger = inject(LoggerService);
    private _addressService = inject(AddressService);
    private _req = inject(RequestHandlerService);

    assignableStores = signal<string[] | undefined>(undefined);
    isUpdatingStoreAssignment = signal(false);
    isValidAssignableStores = computed(() => this.assignableStores()?.length > 0);

    constructor() {
        toObservable(this._addressService.selectedAddress)
            .pipe(takeUntilDestroyed())
            .subscribe(async address => {
                this._logger.debug('StoreAssignmentService -- selected address changed:', address);
                await this.computeStoreAssignment(address ? {latitude: address.lat, longitude: address.lng} : undefined);
            });
    }

    async computeStoreAssignment(coordinates: Coordinate | undefined) {
        this._logger.debug('StoreAssignmentService -- updating assignable stores');

        if (!coordinates) {
            this._logger.debug('StoreAssignmentService -- no location selected. clearing assignable stores');
            this.assignableStores.set(undefined);
            return;
        }

        const url = environment.apiUrl + 'StoreAssignment/Calculate';

        const request = this._http.get<{stores: string[]}>(url, {
            params: {lat: coordinates.latitude.toString(), lng: coordinates.longitude.toString()}
        });

        const response = await this._req.requesterHandler<{stores: string[]}>(request, this.isUpdatingStoreAssignment);
        if (response.type === 'error') {
            this._logger.debug('StoreAssignmentService -- error while updating assignable stores:', response.error);
            return;
        }

        const assignableStores = response.data.stores;

        this._logger.debug('StoreAssignmentService -- got assignable stores from API:', assignableStores);
        this.assignableStores.set(assignableStores);
    }
}
